import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RouteComponentProps } from 'react-router-dom'
import jwtDecode, { JwtPayload } from 'jwt-decode'

import { AppThunk } from 'app/store'
import { postLoginV1API } from 'api/loginAPI'
import { AccountTransferStatusData } from 'api/v2/accountTransferAPI'
import { RootState } from 'app/rootReducer'

export interface LicensePayload extends JwtPayload {
  lkey: string
  lmu: number
  lexp: number
}

export interface TokenPayload extends JwtPayload {
  type: string
  email: string
  groupUUID: string
  groupTag: string
  migrate?: boolean
}

type AuthState = {
  isProcessing: boolean
  error: string
  token: string
  payload: LicensePayload
  payloadV2: TokenPayload
  systemVersion: string
  transferStatus: AccountTransferStatusData
  authAction: string
}

const restoreToken: string = window.localStorage.getItem('token') || ''
const restoreTokenPayload: LicensePayload = restoreToken
  ? jwtDecode(restoreToken)
  : {
      lkey: '',
      lmu: 0,
      lexp: 0,
    }

const restoreTokenV2: string = window.localStorage.getItem('tokenV2') || ''
const restoreTokenPayloadV2: TokenPayload = restoreTokenV2
  ? jwtDecode(restoreTokenV2)
  : { type: '', email: '', groupUUID: '', groupTag: '' }

export const initTransferStatusValue = {
  isTransferring: false,
  transferTo: '',
  transferToken: '',
  transferExpiredTime: 0,
}

const restoreTransferStatus: AccountTransferStatusData = JSON.parse(window.localStorage.getItem('transferStatus') || JSON.stringify(initTransferStatusValue))

const initialState: AuthState = {
  isProcessing: false,
  error: '',
  token: restoreTokenV2 ? restoreTokenV2 : restoreToken,
  payload: restoreTokenPayload,
  payloadV2: restoreTokenPayloadV2,
  systemVersion: restoreTokenV2 ? 'v2' : 'v1',
  transferStatus: restoreTransferStatus,
  authAction: '',
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    actionStart(state) {
      state.isProcessing = true
      state.error = ''
    },
    actionFailure(state, action: PayloadAction<string>) {
      state.isProcessing = false
      state.error = action.payload
    },
    actionSuccess(state, action: PayloadAction<string>) {
      state.isProcessing = false
      state.error = ''
      state.token = action.payload
      state.payload = jwtDecode(action.payload)
    },
    setTokenV2(state, action: PayloadAction<string>) {
      state.token = action.payload
      state.payloadV2 = jwtDecode(action.payload)
    },
    setSystemVersion(state, action: PayloadAction<string>) {
      state.systemVersion = action.payload
    },
    resetAuth(state) {
      state.token = ''
      state.payload = {
        lkey: '',
        lmu: 0,
        lexp: 0,
      }
      state.payloadV2 = { type: '', email: '', groupUUID: '', groupTag: '' }
    },
    setTransferStatus(state, action: PayloadAction<AccountTransferStatusData>) {
      state.transferStatus = action.payload
    },
    setAuthAction(state, action: PayloadAction<string>) {
      state.authAction = action.payload
    }
  },
})

export const { 
  actionStart,
  actionFailure,
  actionSuccess,
  setTokenV2,
  setSystemVersion,
  resetAuth,
  setAuthAction,
} = authSlice.actions

export const selectAuth = (state: RootState) => state.auth

export const selectAuthToken = (state: RootState) => state.auth.token

export const selectAuthTokenPayload = (state: RootState) => state.auth.payload

export const selectAuthTokenPayloadV2 = (state: RootState) => state.auth.payloadV2

export const selectTransferStatus = (state: RootState) => state.auth.transferStatus

export const selectAuthAction = (state: RootState) => state.auth.authAction

export default authSlice.reducer

// Thunk start:
export const postLoginV1Thunk =
  (key: string, secret: string, history: RouteComponentProps['history']): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(actionStart())
      const { token } = await postLoginV1API(key, secret)

      window.localStorage.setItem('token', token)
      dispatch(actionSuccess(token))
      history.push('/users')
    } catch (err) {
      if (err && err.response && err.response.data && err.response.data.msg) {
        dispatch(actionFailure(err.response.data.msg))
      } else {
        dispatch(actionFailure(err))
      }

      // TODO: handle fail flow
    }
  }

export const resetAuthInfoThunk = (): AppThunk => async (dispatch) => {
  dispatch(resetAuth())
  localStorage.removeItem('token')
  localStorage.removeItem('tokenV2')
  localStorage.removeItem('transferStatus')
  window.sessionStorage.clear()
}

export const logoutThunk =
  (history: RouteComponentProps['history']): AppThunk =>
  async (dispatch) => {
    dispatch(resetAuthInfoThunk())
    history.push('/signin')
  }

export const setTokenV2Thunk =
  (token: string): AppThunk =>
  async (dispatch) => {
    dispatch(setTokenV2(token))
    window.localStorage.setItem('tokenV2', token)
  }

export const setTransferStatus =
  (transferStatus: AccountTransferStatusData): AppThunk =>
  async (dispatch) => {
    dispatch(authSlice.actions.setTransferStatus(transferStatus))
    window.localStorage.setItem('transferStatus', JSON.stringify(transferStatus))
  }
